import { FEES, ABSTRACTS, CONFERENCE } from "@/data/content";
import Reveal from "@/components/Reveal";
import { ExternalLink, Download, Mail, Check } from "lucide-react";

export default function Registration() {
  return (
    <section id="register" className="py-24 sm:py-32 px-5 sm:px-8 lg:px-12 bg-paper">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-crimson font-semibold mb-5">06 · Registration</p>
          <h2 className="font-heading text-4xl sm:text-6xl font-bold tracking-tighter leading-[0.95] max-w-3xl">
            Secure your seat at BioInnovate 2026.
          </h2>
          <p className="mt-5 text-lg text-ink/70 max-w-2xl">
            Your registration includes access to the AI Builder's Workshop and the Startup Landscape Track.
          </p>
        </Reveal>

        {/* Fee table */}
        <Reveal delay={0.06}>
          <div className="mt-14 border border-ink/10 overflow-hidden">
            <div className="grid grid-cols-3 bg-ink text-white font-mono text-[11px] sm:text-xs uppercase tracking-wider">
              {FEES.headers.map((h) => (
                <div key={h} className="p-4 sm:p-5 border-r border-white/10 last:border-r-0">{h}</div>
              ))}
            </div>
            {FEES.rows.map((row, i) => (
              <div key={row.category} className={`grid grid-cols-3 ${i % 2 ? "bg-white" : "bg-paper"} border-t border-ink/10`}>
                <div className="p-4 sm:p-5 font-medium text-sm sm:text-base border-r border-ink/10">{row.category}</div>
                <div className="p-4 sm:p-5 font-mono text-crimson font-semibold border-r border-ink/10">{row.regular}</div>
                <div className="p-4 sm:p-5 font-mono">{row.spot}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-5 gap-10 lg:gap-12">
          {/* Form embed */}
          <div className="lg:col-span-3">
            <Reveal>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-2xl font-semibold tracking-tight">Registration Form</h3>
                <a
                  href={CONFERENCE.registerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-form-new-tab"
                  className="font-mono text-xs text-crimson font-semibold flex items-center gap-1.5 hover:underline underline-offset-4"
                >
                  Open in new tab <ExternalLink size={14} />
                </a>
              </div>
              <div className="border border-ink/10 bg-white overflow-hidden">
                <iframe
                  src={CONFERENCE.registerEmbedUrl}
                  title="BioInnovate 2026 Registration Form"
                  className="w-full h-[640px]"
                  data-testid="registration-iframe"
                >
                  Loading…
                </iframe>
              </div>
            </Reveal>
          </div>

          {/* Abstracts */}
          <div className="lg:col-span-2">
            <Reveal delay={0.08}>
              <div id="abstracts" className="border border-ink/10 bg-ink text-white p-8 h-full flex flex-col">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-crimson font-semibold mb-3">Call for Abstracts</p>
                <h3 className="font-heading text-2xl font-semibold tracking-tight">Present your research.</h3>
                <p className="mt-4 text-white/70 text-[15px] leading-relaxed">{ABSTRACTS.body}</p>
                <ul className="mt-6 space-y-2.5 text-sm text-white/80">
                  {["250–300 words · Times New Roman 12pt", "Structured: Background · Objectives · Methodology · Results · Conclusion", "Published in proceedings; selected papers invited for full publication"].map((x) => (
                    <li key={x} className="flex items-start gap-2.5">
                      <Check size={16} className="text-crimson shrink-0 mt-0.5" /> <span>{x}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-8 flex flex-col gap-3">
                  <a
                    href={CONFERENCE.abstractTemplate}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-abstract-template"
                    className="bg-crimson text-white font-semibold px-6 py-3.5 flex items-center justify-center gap-2 hover:bg-white hover:text-ink transition-colors"
                  >
                    <Download size={17} /> Download Template
                  </a>
                  <a
                    href={`mailto:${CONFERENCE.email}?subject=BioInnovate 2026 Abstract Submission`}
                    data-testid="email-abstract"
                    className="border border-white/30 text-white font-semibold px-6 py-3.5 flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
                  >
                    <Mail size={17} /> Email your abstract
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
